import { Button, FormControl, FormHelperText, FormLabel, Grid, Input, Stack, Typography } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { OrderButtons } from '@/components/form-elements/OrderButtons';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { SignCodeAutocomplete } from '@/components/form-elements/SignCodeAutocomplete';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { SignCodeIcon } from '@/components/signs/SignCodeIcon';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { I18N_DE } from '@/i18n/de';
import {
  CreateSignDto,
  SignCode,
  SignPermanency,
  SignReflectionClass,
  SignSize,
  SignSourceType,
  SignStatusType,
} from '@/modules/api';
import {
  backendApiSignGroupsRemoveMutation,
  backendApiSignsCreateMutation,
  backendApiSignSupportsRemoveMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useSignSupportQuery } from '@/queries/useSignSupportQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { CreateSignStep } from '@/types/CreateSignStep';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId_/$signGroupId/hinzufuegen',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: CreateSignRoute,
});

function CreateSignRoute() {
  const { adminLevelId, signSupportId, signGroupId } = Route.useParams();
  const navigate = Route.useNavigate();
  const formId = useId();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: group } = useSignGroupQuery();
  const { data: support } = useSignSupportQuery();

  const step = useCreateSignStore((state) => state.step);
  const { resetStore, reloadSupportLayer } = useCreateSignStore((state) => state.actions);

  const {
    control,
    watch,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<
    Pick<CreateSignDto, 'order' | 'size' | 'reflectionClass' | 'value' | 'permanency'> & {
      signCode: SignCode;
    }
  >();

  const signCode = watch('signCode');
  const currentOrder = Number(watch('order'));

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignsCreateMutation(),
    onSuccess: ({ id }) => {
      reloadSupportLayer();

      if ([CreateSignStep.SIGN, CreateSignStep.ADD_SIGN].includes(step)) resetStore();
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId/$signId',
        params: {
          adminLevelId,
          signSupportId,
          signGroupId,
          signId: id,
        },
      });
    },
  });

  const { mutateAsync: removeSupportMutate, isPending: removeSupportIsPending } = useMutation({
    ...backendApiSignSupportsRemoveMutation(),
    onSuccess: async () =>
      navigate({
        to: '/verkehrszeichen/$adminLevelId',
        params: { adminLevelId },
      }),
  });

  const { mutateAsync: removeGroupMutate, isPending: removeGroupIsPending } = useMutation({
    ...backendApiSignGroupsRemoveMutation(),
    onSuccess: async () =>
      navigate({
        to: '/verkehrszeichen/$adminLevelId',
        params: { adminLevelId },
      }),
  });

  const groupsCount = support?.groups?.length || 0;

  const signs =
    group?.signs?.map(({ id, code, status }) => ({
      id,
      code,
      description: undefined,
      status,
    })) || [];

  return (
    group && (
      <>
        <EditModalDialog
          title="Neues Verkehrszeichen hinzufügen"
          onClose={
            step === CreateSignStep.SIGN
              ? () => {
                  setConfirmOpen(true);
                }
              : undefined
          }
        >
          <Stack
            component="form"
            id={formId}
            onSubmit={handleSubmit(({ signCode: { code }, order, size, reflectionClass, value, permanency }) => {
              if (isPending) return;

              mutate({
                body: {
                  code,
                  groupId: group.id,
                  order,
                  source: SignSourceType.MANUAL,
                  size,
                  reflectionClass,
                  value,
                  permanency,
                },
              });
            })}
            gap={2}
          >
            <Grid container spacing={2}>
              <Grid xs={10}>
                <FormControl error={!!errors.signCode}>
                  <FormLabel>Verkehrszeichen</FormLabel>
                  <Controller
                    control={control}
                    name="signCode"
                    rules={{ required: true }}
                    defaultValue={undefined}
                    render={({ field }) => (
                      <SignCodeAutocomplete
                        onChange={(event, value) => {
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid xs={2}>
                <FormControl
                  error={!!errors.order}
                  disabled={!signCode}
                  sx={{ alignItems: 'center', justifyContent: 'center' }}
                >
                  <FormLabel sx={{ marginBottom: '13px' }}>Position</FormLabel>
                  <Controller
                    control={control}
                    name="order"
                    defaultValue={group.signs?.length || 0}
                    render={({ field }) => (
                      <OrderButtons value={field.value} onChange={field.onChange} limit={group.signs?.length || 0} />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {[
              ...signs.slice(0, currentOrder),
              ...(signCode
                ? [
                    {
                      id: signCode.code,
                      code: signCode.code,
                      description: signCode.description,
                      status: SignStatusType.DRAFT,
                    },
                  ]
                : []),
              ...signs.slice(currentOrder, signs.length),
            ].map(({ id, code, status, description }) => (
              <Stack key={id} direction="row" gap={1} alignItems="center">
                <SignCodeIcon code={code} style={{ width: 40, height: 40 }} deleted={status === 'DELETED'} />
                <Typography level="body-sm" sx={{ fontSize: 14, textWrap: 'nowrap' }}>
                  {code}
                </Typography>
                <Typography level="body-sm" sx={{ fontSize: 14, fontWeight: 700 }}>
                  {description || ''}
                </Typography>
              </Stack>
            ))}

            <FormControl error={!!errors.size}>
              <FormLabel>Größe</FormLabel>
              <Controller
                control={control}
                name="size"
                defaultValue={undefined}
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    options={Object.values(SignSize)}
                    optionLabel={({ option }) => I18N_DE.SignSize[option]}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.reflectionClass}>
              <FormLabel>Reflektionsklasse</FormLabel>
              <Controller
                control={control}
                name="reflectionClass"
                defaultValue={undefined}
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    options={Object.values(SignReflectionClass)}
                    optionLabel={({ option }) => I18N_DE.SignReflectionClass[option]}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.permanency}>
              <FormLabel>Beschilderungsart</FormLabel>
              <Controller
                control={control}
                name="permanency"
                defaultValue={undefined}
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    options={Object.values(SignPermanency)}
                    optionLabel={({ option }) => I18N_DE.SignPermanency[option]}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.value}>
              <FormLabel>Zusatz</FormLabel>
              <Input
                type="text"
                variant="outlined"
                slots={{
                  input: FloatingInput,
                }}
                slotProps={{
                  input: {
                    ...register('value'),
                    label: 'optional',
                  },
                }}
              />
            </FormControl>

            {error && <FormHelperText>{error.message}</FormHelperText>}

            <Button type="submit" form={formId} disabled={!signCode || isPending} loading={isPending}>
              Speichern
            </Button>
          </Stack>
        </EditModalDialog>
        <ConfirmationModal
          title={`${groupsCount === 1 ? 'Aufstellvorrichtung' : 'Gruppe'} löschen`}
          content="Sind Sie sicher?"
          open={confirmOpen}
          onAbort={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            if (removeSupportIsPending || removeGroupIsPending) return;

            await (groupsCount === 1
              ? removeSupportMutate({ path: { signSupportId } })
              : removeGroupMutate({ path: { signGroupId } }));
          }}
        />
      </>
    )
  );
}
